import * as React from "react";
import { Select } from "../components/Form";
import { Icon } from "../components/Icon";
import { useAsync } from "../general/useAsync";
import * as Management from "../management/ManagementStore";

/**
 * Picks the qualification module a screen is working in.
 *
 * Almost everything downstream of the catalogue - the assessment tree, mark entry, procedure
 * assignment, calculation - is scoped to one qualification module, so the same picker appears on
 * several screens. Its options need the composed label (module, cohort, cycle) because a
 * qualification module has no name of its own.
 */

export interface ModulePickerProps {
    value: string;
    onChange: (qualificationModuleID: string) => void;
    label?: string;
    className?: string;
}

export const ModulePicker: React.FC<ModulePickerProps> = ({ value, onChange, label = "Qualification module", className = "" }) => {
    const modules = useAsync(() => Management.listQualificationModules(), [], { initial: [] });

    const options = React.useMemo(
        () =>
            (modules.data ?? [])
                .map((m) => ({ id: m.id ?? "", label: Management.qualificationModuleLabel(m) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [modules.data]
    );

    return (
        <div className={`min-w-72 flex-1 ${className}`}>
            <label className="mb-1 block text-sm font-medium text-text">{label}</label>
            <Select
                value={value}
                placeholder={modules.isLoading ? "Loading..." : "Select a qualification module"}
                onChange={(e) => onChange(e.target.value)}
                disabled={modules.isLoading}
            >
                {options.map((o) => (
                    <option key={o.id} value={o.id}>
                        {o.label}
                    </option>
                ))}
            </Select>
            {modules.error && (
                <p className="mt-1 flex items-center gap-1 text-sm text-danger-text">
                    <Icon icon={["fad", "triangle-exclamation"]} />
                    {modules.error}
                </p>
            )}
            {!modules.isLoading && !modules.error && options.length === 0 && (
                <p className="mt-1 text-xs text-text-weak">
                    No qualification modules exist yet. Create one under Management before using this screen.
                </p>
            )}
        </div>
    );
};

export default ModulePicker;
